/**
 * ExportResultToast — Cartão de resultado da exportação
 * -----------------------------------------------------------------------------
 * Substitui o aviso simples do sistema por um cartão fixo no canto inferior
 * direito, com título recolhível, ícone do tipo de arquivo, nome do arquivo e
 * a ação correspondente (baixar novamente / tentar novamente).
 *
 * Componente genérico: qualquer módulo que use o ExportDialog o recebe pronto.
 *
 * @version 1.0.0
 * RELEASE: 2026-09-07 v1.0.0 - Criação inicial.
 */

import { useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Download,
  FileSpreadsheet,
  FileText,
  Loader2,
  Mail,
  RotateCcw,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ExportDelivery, ExportFormat } from '@/lib/export/types';

export type ExportResultStatus = 'processing' | 'success' | 'error';

export interface ExportResultState {
  status: ExportResultStatus;
  /** Nome do arquivo gerado (ou sugerido, enquanto processa) */
  fileName: string;
  format: ExportFormat;
  delivery: ExportDelivery;
  /** Mensagem secundária (sucesso ou causa do erro) */
  message: string;
  /** Total de registros do arquivo */
  rowCount?: number;
}

interface ExportResultToastProps {
  state: ExportResultState | null;
  onClose: () => void;
  /** Refaz o download local do arquivo já gerado */
  onRedownload?: () => void;
  /** Repete a exportação após falha */
  onRetry?: () => void;
}

export function ExportResultToast({
  state,
  onClose,
  onRedownload,
  onRetry,
}: ExportResultToastProps) {
  const [recolhido, setRecolhido] = useState(false);

  if (!state) return null;

  const { status, fileName, format, delivery, message, rowCount } = state;
  const IconeArquivo = format === 'csv' ? FileText : FileSpreadsheet;

  /** Título da faixa superior conforme o estado */
  const titulo =
    status === 'processing'
      ? 'Preparando seu arquivo...'
      : status === 'error'
        ? 'Não foi possível gerar o arquivo'
        : delivery === 'email'
          ? 'O arquivo foi enviado'
          : 'O arquivo está pronto';

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-4 z-[70] flex justify-center px-4 sm:inset-x-auto sm:right-6 sm:justify-end">
      <div
        className={cn(
          'pointer-events-auto w-full max-w-[420px] overflow-hidden rounded-xl border border-border/70',
          'bg-background shadow-2xl animate-in slide-in-from-bottom-4 fade-in duration-300',
        )}
      >
        {/* ===== Faixa superior ===== */}
        <div className="flex items-center gap-2 px-4 py-3">
          {status === 'processing' ? (
            <Loader2 className="h-5 w-5 shrink-0 animate-spin text-primary" />
          ) : status === 'error' ? (
            <AlertCircle className="h-5 w-5 shrink-0 text-destructive" />
          ) : (
            <CheckCircle2 className="h-5 w-5 shrink-0 fill-emerald-600 text-background" />
          )}

          <span className="flex-1 truncate text-sm font-medium text-foreground">{titulo}</span>

          <button
            type="button"
            onClick={() => setRecolhido((v) => !v)}
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label={recolhido ? 'Expandir' : 'Recolher'}
          >
            {recolhido ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>

          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-1.5 py-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            Fechar
          </button>
        </div>

        {/* ===== Detalhe do arquivo ===== */}
        {!recolhido && (
          <div className="flex items-center gap-3 border-t border-border/60 px-4 py-3.5">
            <div
              className={cn(
                'flex h-12 w-12 shrink-0 items-center justify-center rounded-lg',
                status === 'error'
                  ? 'bg-destructive/10 text-destructive'
                  : delivery === 'email'
                    ? 'bg-primary/10 text-primary'
                    : 'bg-emerald-600/10 text-emerald-700 dark:text-emerald-400',
              )}
            >
              {delivery === 'email' && status === 'success' ? (
                <Mail className="h-6 w-6" />
              ) : (
                <IconeArquivo className="h-6 w-6" />
              )}
            </div>

            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-foreground">{fileName}</p>
              <p className="line-clamp-2 text-xs text-muted-foreground">
                {message}
                {typeof rowCount === 'number' && status === 'success'
                  ? ` · ${rowCount.toLocaleString('pt-BR')} registro(s)`
                  : ''}
              </p>
            </div>

            {status === 'success' && delivery === 'download' && onRedownload && (
              <Button variant="outline" size="sm" onClick={onRedownload} className="shrink-0">
                <Download className="mr-1.5 h-3.5 w-3.5" />
                Baixar
              </Button>
            )}

            {status === 'error' && onRetry && (
              <Button variant="outline" size="sm" onClick={onRetry} className="shrink-0">
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                Tentar
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
